import classNames from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Transaction } from "@common/types/transaction";
import { EagerLoaded } from "@common/types/base/orm";
import { useOverlays } from "contexts/OverlayContext";
import { MatchedTransactionCard } from "@components/transactions/MatchedTransactionCard/MatchedTransactionCard";
import shellStyles from "@components/transactions/MatchedTransactionCard/MatchCardShell.module.scss";
import { useUnmatchTransfer } from "@mutations/transaction";
import { ClickableContainer } from "@design-system/ClickableContainer/ClickableContainer";
import { HStack } from "@design-system/HStack/HStack";
import { SearchIcon } from "@icons";
import { RECEIPT_UPLOAD_HIGHLIGHTED_ELEMENT } from "../../ReceiptUpload/lib";

import styles from "./TransactionActionMatchTransfer.module.scss";

interface Props {
    required: boolean;
    transaction: EagerLoaded<
        Transaction,
        "transactionMatches" | "pairedTransfer"
    >;
}

export const TransactionActionsMatchTransfer: React.FC<Props> = ({
    transaction,
    required,
}) => {
    const { openOverlay } = useOverlays();
    const [pairedTransaction, setPairedTransaction] = useState(
        transaction?.pairedTransfer,
    );

    useEffect(() => {
        setPairedTransaction(transaction?.pairedTransfer);
    }, [transaction?.pairedTransfer]);

    const unlinkMatch = useUnmatchTransfer({
        financialTransaction: transaction,
        onSuccess: () => {
            setPairedTransaction(null);
        },
    });

    const handleOpenMatchDrawer = useCallback(
        () =>
            openOverlay("transactionMatch", { transactionId: transaction.id }),
        [openOverlay, transaction],
    );

    const handleDetach = useCallback(async () => {
        try {
            await unlinkMatch.mutateAsync();
        } catch {
            // error toast handled by axios interceptor; swallow the
            // rejection so callers invoking this via `void onDetach()`
            // don't produce unhandled promise rejections.
        }
    }, [unlinkMatch]);

    return (
        <div
            className={classNames(
                shellStyles.shell,
                RECEIPT_UPLOAD_HIGHLIGHTED_ELEMENT,
                styles.transactionActionMatch,
                required && !pairedTransaction && styles.required,
            )}
        >
            {pairedTransaction ? (
                <MatchedTransactionCard
                    label="Matched transaction"
                    pairedTransaction={pairedTransaction}
                    onClick={handleOpenMatchDrawer}
                    onDetach={handleDetach}
                />
            ) : (
                <Tooltip content="Link a transaction match for this transfer">
                    <div>
                        <HStack
                            as={ClickableContainer}
                            onClick={handleOpenMatchDrawer}
                            justify="between"
                            align="center"
                            className={classNames(
                                "mb-0 cursor-pointer",
                                shellStyles.control,
                                styles.findMatchControl,
                            )}
                        >
                            <span>Find a transaction match</span>
                            <SearchIcon />
                        </HStack>
                    </div>
                </Tooltip>
            )}
        </div>
    );
};
